<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import axios from 'axios'
import ComparePlayersOverlay from './ComparePlayersOverlay.vue'

const props = defineProps({
  content: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['roster-updated', 'open-player-profile'])

const search = ref('')
const searchResults = ref([])
const loading = ref(false)
const activeTab = ref('latest') // 'latest' or 'search'
const savingPlayers = ref(new Set()) // Track which players are being saved
const showPlayerPopup = ref(false)
const selectedPlayer = ref(null)
const playerDetails = ref(null)
const loadingPlayerDetails = ref(false)
let debounceTimeout = null
const selectedPlayers = ref({})
const contextMenu = ref({ visible: false, x: 0, y: 0 })
const comparisonLoading = ref(false)
const comparisonError = ref(null)
const comparisonResult = ref(null)
const showComparisonOverlay = ref(false)
const activeComparePlayerId = ref(null)
const selectedGamesByPlayer = ref({})
const tableContextMenu = ref({ visible: false, x: 0, y: 0 })
const moveDialog = ref({ open: false, targetPlayerId: '' })
const moveLoading = ref(false)
const moveError = ref(null)
const moveSuccess = ref(null)
const manualAddId = ref('')
const manualAddName = ref('')
const overlaySearch = ref('')
const overlaySearchResults = ref([])
const overlaySearchLoading = ref(false)
let overlaySearchDebounce = null
const playerStats = ref({})
const rosterHoverPlayer = ref(null)
const playerResources = ref({})
const resourcesLoading = ref(false)
const resourcesError = ref(null)
const activePlayerTab = ref('boxscores')
const selectedContextMenu = ref({ visible: false, x: 0, y: 0, player: null })

const tabCount = (type) => {
  const pid = toIdString(activeComparePlayerId.value)
  if (!pid) return 0
  const stats = playerStats.value[pid] || {}
  const resources = playerResources.value[pid] || {}
  const fallback = {
    boxscores: (comparisonGamesByPlayer.value[pid] || []).length,
    rosters: resources.rosters?.length || 0,
    projections: resources.projections?.length || 0,
    balanced: resources.balanced_lines?.length || 0
  }
  switch (type) {
    case 'boxscores':
      return stats.boxscores ?? fallback.boxscores
    case 'rosters':
      return stats.rosters_count ?? fallback.rosters
    case 'projections':
      return stats.projections ?? fallback.projections
    case 'balanced':
      return stats.balanced_lines ?? fallback.balanced
    default:
      return 0
  }
}

// Create a local roster state that we can update optimistically
const localRoster = ref([...(props.content?.roster || props.content?.players || [])])

const teamRoster = computed(() => localRoster.value)
const selectedPlayerIds = computed(() => Object.keys(selectedPlayers.value))
const selectedPlayersList = computed(() => Object.values(selectedPlayers.value))
const moveCandidatePlayers = computed(() => {
  return selectedPlayersList.value.filter(p => p.id !== activeComparePlayerId.value)
})
const comparisonGamesByPlayer = computed(() => {
  const result = {}
  const games = comparisonResult.value?.data || []
  games.forEach(game => {
    const pid = toIdString(game.player_id || game.playerId)
    if (!pid) return
    if (!result[pid]) result[pid] = []
    result[pid].push(game)
  })
  return result
})

const gamesForActivePlayer = computed(() => {
  if (!activeComparePlayerId.value) return []
  return comparisonGamesByPlayer.value[activeComparePlayerId.value] || []
})

const selectedGameIdsForActive = computed(() => {
  if (!activeComparePlayerId.value) return new Set()
  return new Set(selectedGamesByPlayer.value[activeComparePlayerId.value] || [])
})

const isAllGamesSelectedForActive = computed(() => {
  const games = gamesForActivePlayer.value
  if (!games.length) return false
  return games.every(g => selectedGameIdsForActive.value.has(String(g.id)))
})

const toggleGameSelection = (game) => {
  if (!activeComparePlayerId.value) return;
  const pid = activeComparePlayerId.value;
  const current = new Set(selectedGamesByPlayer.value[pid] || []);
  const key = String(game.id);
  if (current.has(key)) {
    current.delete(key);
  } else {
    current.add(key);
  }
  selectedGamesByPlayer.value = {
    ...selectedGamesByPlayer.value,
    [pid]: Array.from(current)
  };
};

const toggleSelectAllForActive = () => {
  const pid = activeComparePlayerId.value;
  if (!pid) return;
  if (isAllGamesSelectedForActive.value) {
    selectedGamesByPlayer.value = { ...selectedGamesByPlayer.value, [pid]: [] };
  } else {
    const allIds = gamesForActivePlayer.value.map(g => String(g.id));
    selectedGamesByPlayer.value = { ...selectedGamesByPlayer.value, [pid]: allIds };
  }
};

const isGameSelected = (game) => {
  return selectedGameIdsForActive.value.has(String(game.id));
};

const removePlayerFromSelection = async (player) => {
  const pid = toIdString(player.id);
  const gameCount = playerGamesCount(pid);

  if (gameCount > 0) {
    const confirmDeleteGames = window.confirm(`Player has ${gameCount} games. Remove player and delete these games?`);
    if (!confirmDeleteGames) return;
    try {
      await axios.post('/api/compare-players/delete-player', {
        player_id: pid,
        delete_games: true
      });
    } catch (e) {
      console.error('Failed to delete player games', e);
      return;
    }
  } else {
    const confirmRemove = window.confirm('Remove player from selection?');
    if (!confirmRemove) return;
    try {
      await axios.post('/api/compare-players/delete-player', {
        player_id: pid,
        delete_games: false
      });
    } catch (e) {
      console.error('Failed to remove player', e);
      return;
    }
  }

  const updated = { ...selectedPlayers.value };
  delete updated[pid];
  selectedPlayers.value = updated;

  if (activeComparePlayerId.value === pid) {
    const remaining = Object.keys(updated);
    activeComparePlayerId.value = remaining[0] || null;
    if (!remaining.length) {
      showComparisonOverlay.value = false;
      comparisonResult.value = null;
      selectedGamesByPlayer.value = {};
      return;
    }
  }

  await fetchComparisonData();
};

const removeFromSelectedOnly = (player) => {
  const pid = toIdString(player.id);
  const updated = { ...selectedPlayers.value };
  delete updated[pid];
  selectedPlayers.value = updated;
  if (activeComparePlayerId.value === pid) {
    const remaining = Object.keys(updated);
    activeComparePlayerId.value = remaining[0] || null;
    if (!remaining.length) {
      showComparisonOverlay.value = false;
      comparisonResult.value = null;
      selectedGamesByPlayer.value = {};
      return;
    }
  }
};

const onPlayerNameBlur = async (player, event) => {
  const newName = (event?.target?.innerText || '').trim();
  if (!newName || newName === player.name) {
    event.target.innerText = player.name;
    return;
  }

  const updatePlayers = window.confirm('Update player name in players table?');
  const updateBoxscores = window.confirm('Also update player name in boxscores?');
  if (!updatePlayers && !updateBoxscores) {
    event.target.innerText = player.name;
    return;
  }

  try {
    await axios.post('/api/compare-players/update-name', {
      player_id: player.id,
      name: newName,
      update_players: updatePlayers,
      update_boxscores: updateBoxscores
    });
    // Update local name
    selectedPlayers.value = {
      ...selectedPlayers.value,
      [player.id]: { ...selectedPlayers.value[player.id], name: newName }
    };
  } catch (e) {
    console.error('Failed to update player name', e);
    event.target.innerText = player.name;
  }
};

const fetchPlayerResources = async (playerId) => {
  if (!playerId) return;
  resourcesLoading.value = true;
  resourcesError.value = null;
  try {
    const resp = await axios.get(`/api/compare-players/resources/${playerId}`);
    playerResources.value = {
      ...playerResources.value,
      [toIdString(playerId)]: resp.data
    };
  } catch (e) {
    resourcesError.value = 'Failed to load player resources';
    console.error('fetchPlayerResources error', e);
  } finally {
    resourcesLoading.value = false;
  }
};

const openSelectedContextMenu = (event, player) => {
  event.preventDefault();
  selectedContextMenu.value = {
    visible: true,
    x: event.clientX,
    y: event.clientY,
    player
  };
  document.addEventListener('click', closeSelectedContextMenu);
};

const closeSelectedContextMenu = () => {
  selectedContextMenu.value = { visible: false, x: 0, y: 0, player: null };
  document.removeEventListener('click', closeSelectedContextMenu);
};

const handleOpenCompareOverlay = (event) => {
  const tab = event?.detail?.tab;
  showComparisonOverlay.value = true;
  // The overlay component will handle its own state
};

const openMoveDialog = () => {
  moveDialog.value = { open: true, targetPlayerId: '' };
  tableContextMenu.value.visible = false;
  moveError.value = null;
  moveSuccess.value = null;
};

const moveSelectedGames = async () => {
  if (!activeComparePlayerId.value || !selectedGameIdsForActive.value.size || !moveDialog.value.targetPlayerId) {
    moveError.value = 'Select games and target player ID.';
    return;
  }
  moveLoading.value = true;
  moveError.value = null;
  moveSuccess.value = null;
  try {
    await axios.post('/api/compare-players/move-games', {
      source_player_id: activeComparePlayerId.value,
      target_player_id: moveDialog.value.targetPlayerId,
      game_ids: Array.from(selectedGameIdsForActive.value)
    });
    moveSuccess.value = 'Games moved successfully.';
    // Clear selections for source
    selectedGamesByPlayer.value = { ...selectedGamesByPlayer.value, [activeComparePlayerId.value]: [] };
    await fetchComparisonData();
  } catch (error) {
    moveError.value = 'Failed to move games';
    console.error('Error moving games:', error);
  } finally {
    moveLoading.value = false;
  }
};

watch(activeComparePlayerId, (pid) => {
  if (pid) {
    fetchComparisonData();
    fetchPlayerResources(pid);
  }
});

const addManualPlayerToSelection = () => {
  const id = toIdString(manualAddId.value).trim();
  if (!id) return;
  if (selectedPlayers.value[id]) {
    manualAddId.value = '';
    manualAddName.value = '';
    return;
  }
  const name = manualAddName.value?.trim() || `Player ${id}`;
  selectedPlayers.value = {
    ...selectedPlayers.value,
    [id]: { id, name }
  };
  manualAddId.value = '';
  manualAddName.value = '';
  compareSelectedPlayers();
};

const addOverlaySearchPlayer = (player, keepListOpen = false) => {
  const id = toIdString(player.player_id || player.id);
  if (!id) return;
  const name = player.player_name || player.name || `Player ${id}`;
  if (!selectedPlayers.value[id]) {
    selectedPlayers.value = { ...selectedPlayers.value, [id]: { id, name } };
    compareSelectedPlayers();
  }
  if (!keepListOpen) {
    overlaySearch.value = '';
    overlaySearchResults.value = [];
  }
};

watch(overlaySearch, (val) => {
  if (!val || val.length < 2) {
    overlaySearchResults.value = [];
    return;
  }
  if (overlaySearchDebounce) clearTimeout(overlaySearchDebounce);
  overlaySearchDebounce = setTimeout(async () => {
    overlaySearchLoading.value = true;
    try {
      const response = await axios.get(`/api/players/search-players/${val}`);
      overlaySearchResults.value = response.data || [];
    } catch (e) {
      overlaySearchResults.value = [];
    } finally {
      overlaySearchLoading.value = false;
    }
  }, 300);
});

const valueOrId = (p) => toIdString(p?.player_id || p?.id);

// Get team ID from props (deep lookup)
const teamId = computed(() => {
  // Try the most likely deep paths
  return props.content?.team?.team_id || props.content?.team?.id || props.content?.teamId || props.content?.team_id
})

const getPlayerId = (player) => {
  // Handles both flat and nested player objects
  return player?.player_id || player?.id || player?.player?.player_id || player?.player?.id;
};

const toIdString = (val) => val !== undefined && val !== null ? String(val) : '';

const togglePlayerSelection = (player) => {
  const playerId = getPlayerId(player);
  if (!playerId) return;

  const updated = { ...selectedPlayers.value };
  if (updated[playerId]) {
    delete updated[playerId];
  } else {
    updated[playerId] = {
      id: toIdString(playerId),
      name: player?.player_name || player?.name || player?.player?.player_name || 'Unknown'
    };
  }

  selectedPlayers.value = updated;
};

const isPlayerSelected = (player) => {
  const playerId = getPlayerId(player);
  return !!(playerId && selectedPlayers.value[toIdString(playerId)]);
};

const setActiveComparePlayer = (playerId) => {
  activeComparePlayerId.value = toIdString(playerId);
};

const playerGamesCount = (playerId) => {
  return (comparisonGamesByPlayer.value[playerId] || []).length;
};

const isPlayerInRoster = (player) => {
  const playerId = getPlayerId(player);
  return teamRoster.value.some(rosterEntry => {
    const rosterPlayerId = getPlayerId(rosterEntry.player || rosterEntry);
    return rosterPlayerId && playerId && rosterPlayerId == playerId;
  });
};

const closeContextMenu = () => {
  contextMenu.value.visible = false;
  document.removeEventListener('click', closeContextMenu);
};

const openContextMenu = (event) => {
  contextMenu.value = {
    visible: true,
    x: event.clientX,
    y: event.clientY
  };
  document.addEventListener('click', closeContextMenu);
};

const onSearchContextMenu = (event) => {
  if (!selectedPlayerIds.value.length) return;
  event.preventDefault();
  openContextMenu(event);
};

const closeTableContextMenu = () => {
  tableContextMenu.value.visible = false;
  document.removeEventListener('click', closeTableContextMenu);
};

const onTableContextMenu = (event) => {
  if (!selectedGameIdsForActive.value.size) return;
  event.preventDefault();
  tableContextMenu.value = { visible: true, x: event.clientX, y: event.clientY };
  document.addEventListener('click', closeTableContextMenu);
};

const clearSelection = () => {
  selectedPlayers.value = {};
  closeContextMenu();
  showComparisonOverlay.value = false;
  selectedGamesByPlayer.value = {};
};

watch(selectedPlayerIds, (ids) => {
  if (!ids.length) {
    closeContextMenu();
  }
});

watch(selectedPlayersList, (list) => {
  if (!list.length) {
    activeComparePlayerId.value = null;
    showComparisonOverlay.value = false;
    return;
  }
  if (!activeComparePlayerId.value || !list.find(p => p.id === activeComparePlayerId.value)) {
    activeComparePlayerId.value = list[0].id;
  }
});

const fetchComparisonData = async (openOverlayIfNeeded = false) => {
  if (!selectedPlayerIds.value.length) return;
  comparisonLoading.value = true;
  comparisonError.value = null;
  selectedGamesByPlayer.value = {};
  moveError.value = null;
  moveSuccess.value = null;

  try {
    const response = await axios.get('/api/boxscores-management/comparable-data', {
      params: {
        player_ids: selectedPlayerIds.value.join(','),
        per_page: 100
      }
    });

    comparisonResult.value = response.data;
    if (!activeComparePlayerId.value) {
      activeComparePlayerId.value = toIdString(selectedPlayerIds.value[0] || null);
    }
    // Fetch player stats (projections / balanced lines / boxscores / rosters)
    try {
      const statsResp = await axios.get('/api/compare-players/stats', {
        params: { player_ids: selectedPlayerIds.value.join(',') }
      });
      // Normalize keys to strings for consistent lookup
      const normalized = {};
      Object.entries(statsResp.data || {}).forEach(([k, v]) => {
        normalized[toIdString(k)] = v;
      });
      playerStats.value = normalized;
    } catch (e) {
      console.error('Failed to fetch player stats', e);
    }
    await fetchPlayerResources(activeComparePlayerId.value);
    if (openOverlayIfNeeded) {
      showComparisonOverlay.value = true;
    }
  } catch (error) {
    comparisonError.value = 'Failed to load comparable games';
    comparisonResult.value = null;
    console.error('Error fetching comparable data comparison:', error);
  } finally {
    comparisonLoading.value = false;
    closeContextMenu();
  }
};

const compareSelectedPlayers = async () => {
  if (!selectedPlayerIds.value.length) return;
  await fetchComparisonData(true);
};

// Check if a player is currently being saved
const isPlayerSaving = (player) => {
  const playerId = player.id || player.player_id
  return savingPlayers.value.has(playerId)
}

// Function to open player profile popup
const openPlayerProfile = async (player) => {
  const playerId = player.player_id || player.player?.player_id
  selectedPlayer.value = player
  showPlayerPopup.value = true
  loadingPlayerDetails.value = true
  playerDetails.value = null
  
  try {
    const response = await axios.get(`/api/players/full-profile/${playerId}`)
    playerDetails.value = response.data
  } catch (error) {
    console.error('Error fetching player details:', error)
    playerDetails.value = { error: 'Failed to load player details' }
  } finally {
    loadingPlayerDetails.value = false
  }
}

const closePlayerPopup = () => {
  showPlayerPopup.value = false
  selectedPlayer.value = null
  playerDetails.value = null
}

// Function to add player to roster
const addPlayerToRoster = async (player) => {
  if (isPlayerInRoster(player) || isPlayerSaving(player)) return
  
  const playerId = player.id || player.player_id
  savingPlayers.value.add(playerId)
  
  // Optimistically add to local roster
  localRoster.value = [...localRoster.value, player]
  
  try {
    // Debug: log the payload before sending
    console.log('Adding player to roster:', { teamId: teamId.value, playerId })
    // Add to API with both teamId and playerId
    await axios.post('/api/roster/add-player', {
      teamId: teamId.value,
      playerId: playerId
    })
    
    // Emit event to parent to update roster
    emit('roster-updated', {
      action: 'add',
      player: player,
      teamId: teamId.value
    })
  } catch (error) {
    console.error('Error adding player to roster:', error)
    // Revert the optimistic update on error
    localRoster.value = localRoster.value.filter(p => 
      p.id !== player.id && p.player_id !== player.player_id
    )
  } finally {
    savingPlayers.value.delete(playerId)
  }
}

// Function to remove player from roster
const removePlayerFromRoster = async (player) => {
  if (isPlayerSaving(player)) return
  console.log('Removing player from roster:', player)
  const playerId = player.player_id || player.player?.player_id
  savingPlayers.value.add(playerId)
  
  // Optimistically remove from local roster
  localRoster.value = localRoster.value.filter(p => 
    p.id !== player.id && p.player_id !== player.player_id
  )
  
  try {
    // Remove from API with both teamId and playerId
    await axios.post('/api/roster/remove-player', {
      teamId: teamId.value,
      playerId: playerId
    })
    
    // Emit event to parent to update roster
    emit('roster-updated', {
      action: 'remove',
      player: player,
      teamId: teamId.value
    })
  } catch (error) {
    console.error('Error removing player from roster:', error)
    // Revert the optimistic update on error
    localRoster.value = [...localRoster.value, player]
  } finally {
    savingPlayers.value.delete(playerId)
  }
}

// Function to fetch latest players
const fetchLatestPlayers = async () => {
  loading.value = true
  try {
    const response = await axios.get('/api/players/latest')
    searchResults.value = response.data.slice(0, 10) // Get only the latest 10 players
  } catch (e) {
    searchResults.value = []
  } finally {
    loading.value = false
  }
}

// Fetch latest players when component mounts
onMounted(() => {
  if (teamId.value) fetchRosterForTeam(teamId.value);
  fetchLatestPlayers();
  console.log('TeamRosterPanel props.content:', props.content);
  window.addEventListener('open-compare-overlay', handleOpenCompareOverlay);
})

onBeforeUnmount(() => {
  document.removeEventListener('click', closeContextMenu);
  window.removeEventListener('open-compare-overlay', handleOpenCompareOverlay);
});

watch(search, (val) => {
  if (!val) {
    if (activeTab.value === 'latest') {
      fetchLatestPlayers()
    } else {
      searchResults.value = []
    }
    return
  }
  loading.value = true
  if (debounceTimeout) clearTimeout(debounceTimeout)
  debounceTimeout = setTimeout(async () => {
    try {
      const response = await axios.get(`/api/players/search-players/${val}`)
      searchResults.value = response.data
    } catch (e) {
      searchResults.value = []
    } finally {
      loading.value = false
    }
  }, 350)
})

const switchTab = (tab) => {
  activeTab.value = tab
  if (tab === 'latest') {
    fetchLatestPlayers()
  } else {
    searchResults.value = []
  }
}

// Fetch roster for a given teamId
const fetchRosterForTeam = async (teamIdVal) => {
  if (!teamIdVal) return;
  try {
    const response = await axios.get(`/api/roster/get-roster/${teamIdVal}`);
    localRoster.value = response.data || [];
  } catch (error) {
    console.error('Failed to fetch roster:', error);
    localRoster.value = [];
  }
}

// Watch for changes in teamId and fetch roster
watch(teamId, (newTeamId) => {
  if (newTeamId) fetchRosterForTeam(newTeamId);
});
</script>

<template>
  <div class="h-full flex flex-col">
    <div class="flex gap-6 flex-1 h-full min-h-0 bg-gray-50">
      <!-- Search Column (1/4) -->
      <div class="basis-1/4 flex-1 bg-white rounded-xs shadow border border-gray-100 p-4 flex flex-col min-h-0">
        <!-- Tabs -->
        <div class="flex border-b border-gray-200 mb-4">
          <button 
            @click="switchTab('latest')"
            class="flex-1 py-2 px-4 text-center font-medium text-sm"
            :class="activeTab === 'latest' ? 'text-pink-500 border-b-2 border-pink-500' : 'text-gray-500 hover:text-gray-700'"
          >
            Latest Players
          </button>
          <button 
            @click="switchTab('search')"
            class="flex-1 py-2 px-4 text-center font-medium text-sm"
            :class="activeTab === 'search' ? 'text-pink-500 border-b-2 border-pink-500' : 'text-gray-500 hover:text-gray-700'"
          >
            Search Players
          </button>
        </div>

  <!-- Comparison Overlay Component -->
  <ComparePlayersOverlay
    v-model="showComparisonOverlay"
    :selected-players="selectedPlayers"
    :initial-tab="activePlayerTab"
    @players-updated="selectedPlayers = $event"
    @close="showComparisonOverlay = false"
  />
        <!-- Search input (only show in search tab) -->
        <div v-if="activeTab === 'search'" class="mb-4">
          <div class="flex items-center mb-2">
            <i class="fas fa-search text-pink-500 mr-2"></i>
            <span class="font-semibold text-gray-700">Search Players</span>
          </div>
          <input
            v-model="search"
            type="text"
            placeholder="Search players..."
            class="w-full px-3 py-2 border rounded focus:outline-none focus:border-pink-500"
          />
        </div>

        <!-- Latest Players Header (only show in latest tab) -->
        <div v-else class="flex items-center mb-4">
          <i class="fas fa-clock text-pink-500 mr-2"></i>
          <span class="font-semibold text-gray-700">Latest Added Players</span>
        </div>

        <div v-if="loading" class="text-gray-400 italic mb-2">Loading...</div>
        <div 
          v-if="selectedPlayerIds.length" 
          class="flex items-center justify-between text-xs text-gray-600 mb-2"
        >
          <div>{{ selectedPlayerIds.length }} selected</div>
          <button 
            class="text-pink-500 hover:text-pink-600 font-medium" 
            @click="clearSelection"
          >
            Clear
          </button>
        </div>
        <ul 
          v-if="searchResults.length" 
          class="space-y-2 flex-1 overflow-auto min-h-0"
          @contextmenu.prevent="onSearchContextMenu"
          data-no-global-context-menu
        >
          <li 
            v-for="player in searchResults" 
            :key="player.id || player.player_id || player.name" 
            class="flex items-center justify-between bg-gray-50 rounded px-2 py-1 hover:bg-gray-100 transition-colors"
            :class="{ 'ring-1 ring-pink-200 bg-pink-50': isPlayerSelected(player) }"
          >
            <div 
              @click="openPlayerProfile(player)"
              class="flex items-center space-x-2 flex-1 cursor-pointer"
            >
              <input 
                type="checkbox" 
                class="form-checkbox text-pink-500 mr-4"
                :checked="isPlayerSelected(player)"
                @click.stop="togglePlayerSelection(player)"
              />
              <i class="fas fa-user text-gray-400"></i>
              <div class="flex justify-between w-full">
                <div>{{ player.name || player.player_name }}</div>
                <div>id: {{ player.player_id }}</div>
              </div>
            </div>
            <template v-if="isPlayerInRoster(player)">
              <span class="text-xs px-2 py-1 rounded bg-green-100 text-green-700 font-semibold">Added</span>
            </template>
            <template v-else>
              <button
                @click.stop="addPlayerToRoster(player)"
                class="text-sm px-2 py-1 rounded flex items-center space-x-1 min-w-[60px] justify-center"
                :class="{
                  'bg-pink-100 text-pink-700 hover:bg-pink-200': !isPlayerSaving(player),
                  'bg-gray-100 text-gray-500 cursor-wait': isPlayerSaving(player)
                }"
                :disabled="isPlayerSaving(player)"
              >
                <i v-if="isPlayerSaving(player)" class="fas fa-spinner fa-spin mr-1"></i>
                <span>{{ isPlayerSaving(player) ? 'Saving...' : 'Add' }}</span>
              </button>
            </template>
          </li>
        </ul>
        <div v-else-if="!loading" class="text-gray-400 italic flex-1">
          {{ activeTab === 'search' ? 'No players found.' : 'No latest players available.' }}
        </div>

        <div 
          v-if="contextMenu.visible" 
          class="fixed z-50 bg-white shadow-lg rounded-md border border-gray-200 w-56 py-2"
          :style="{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }"
          @click.stop
          data-no-global-context-menu
        >
          <button
            class="w-full text-left px-3 py-2 text-sm hover:bg-pink-50 flex items-center justify-between disabled:opacity-50"
            :disabled="!selectedPlayerIds.length || comparisonLoading"
            @click="compareSelectedPlayers"
          >
            <span>Compare selected</span>
            <span class="text-xs text-gray-500">{{ selectedPlayerIds.length }}</span>
          </button>
          <button
            class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 text-gray-600"
            @click="clearSelection"
          >
            Clear selection
          </button>
        </div>

        <div v-if="comparisonLoading" class="mt-3 text-sm text-gray-500">
          <i class="fas fa-spinner fa-spin mr-2"></i> Loading comparable games...
        </div>
        <div v-else-if="comparisonError" class="mt-3 text-sm text-red-500">
          {{ comparisonError }}
        </div>
        <div v-else-if="comparisonResult?.data?.length" class="mt-3 text-sm text-gray-600">
          Loaded {{ comparisonResult.data.length }} games. <button class="text-pink-500 hover:text-pink-600 font-semibold" @click="showComparisonOverlay = true">Open comparison overlay</button>
        </div>
      </div>

      <!-- Roster Column (3/4) -->
      <div class="basis-3/4 flex-1 bg-white rounded-xs shadow border border-gray-100 p-4 flex flex-col min-h-0">
        <div class="flex items-center mb-2">
          <i class="fas fa-users text-pink-500 mr-2"></i>
          <span class="font-semibold text-gray-700">Current Roster</span>
        </div>
        <ul v-if="teamRoster.length" class="space-y-2 flex-1 overflow-auto min-h-0">
          <li 
            v-for="player in teamRoster" 
            :key="player.id || player.player_id || player.name" 
            class="flex items-center justify-between bg-gray-50 rounded px-2 py-1 hover:bg-gray-100 transition-colors"
            :class="{ 'opacity-50': isPlayerSaving(player) }"
          >
            <div 
              @click="openPlayerProfile(player)"
              class="flex items-center space-x-2 flex-1 cursor-pointer"
            >
              <i class="fas fa-user text-gray-400"></i>
              <span>
                {{ player.player?.player_name || player.name || player.player_name || 'Unknown' }} ( {{ player.player_id }})
              </span>
            </div>
            <div class="flex items-center space-x-2">
              <span v-if="isPlayerSaving(player)" class="text-sm text-gray-500">
                <i class="fas fa-spinner fa-spin mr-1"></i>
                Saving...
              </span>
              <button
                v-else
                @click.stop="removePlayerFromRoster(player)"
                class="text-sm px-2 py-1 rounded flex items-center justify-center transition-all duration-150 group bg-transparent"
                title="Remove from roster"
              >
                <i class="fas fa-trash-alt text-red-400 opacity-30 grayscale group-hover:opacity-100 group-hover:grayscale-0 group-hover:text-red-600 transition-all duration-150"></i>
              </button>
            </div>
          </li>
        </ul>
        <div v-else class="text-gray-400 italic flex-1">No players in roster.</div>
      </div>
    </div>

    <!-- Player Profile Popup Overlay -->
    <div 
      v-if="showPlayerPopup" 
      class="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      @click="closePlayerPopup"
    >
      <div 
        class="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        @click.stop
      >
        <!-- Popup Header -->
        <div class="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <div class="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center">
              <i class="fas fa-user text-pink-500 text-xl"></i>
            </div>
            <div>
              <h3 class="text-xl font-bold text-gray-800">
                {{ selectedPlayer?.name || selectedPlayer?.player_name || selectedPlayer?.player?.player_name || 'Player Profile' }}
              </h3>
              <p class="text-sm text-gray-500">ID: {{ selectedPlayer?.player_id || selectedPlayer?.id || selectedPlayer?.player?.player_id }}</p>
            </div>
          </div>
          <button 
            @click="closePlayerPopup"
            class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>

        <!-- Popup Content -->
        <div class="p-6">
          <!-- Loading State -->
          <div v-if="loadingPlayerDetails" class="flex flex-col items-center justify-center py-12">
            <i class="fas fa-spinner fa-spin text-pink-500 text-3xl mb-4"></i>
            <p class="text-gray-500">Loading player details...</p>
          </div>

          <!-- Error State -->
          <div v-else-if="playerDetails?.error" class="text-center py-12">
            <i class="fas fa-exclamation-circle text-red-500 text-3xl mb-4"></i>
            <p class="text-red-600">{{ playerDetails.error }}</p>
          </div>

          <!-- Player Details -->
          <div v-else-if="playerDetails" class="space-y-6">
            <!-- Basic Info -->
            <div>
              <h4 class="font-semibold text-gray-700 flex items-center mb-3">
                <i class="fas fa-id-card text-pink-500 mr-2"></i>
                Basic Information
              </h4>
              <div class="grid grid-cols-2 gap-4">
                <div class="bg-gray-50 rounded-lg p-4">
                  <div class="text-sm text-gray-500 mb-1">Player Name</div>
                  <div class="font-semibold text-gray-800">
                    {{ playerDetails.player_name || 'N/A' }}
                  </div>
                </div>
                <div class="bg-gray-50 rounded-lg p-4">
                  <div class="text-sm text-gray-500 mb-1">Player ID</div>
                  <div class="font-semibold text-gray-800">
                    {{ playerDetails.player_id || 'N/A' }}
                  </div>
                </div>
                <div v-if="playerDetails.position" class="bg-gray-50 rounded-lg p-4">
                  <div class="text-sm text-gray-500 mb-1">Position</div>
                  <div class="font-semibold text-gray-800">{{ playerDetails.position }}</div>
                </div>
                <div v-if="playerDetails.team" class="bg-gray-50 rounded-lg p-4">
                  <div class="text-sm text-gray-500 mb-1">Team</div>
                  <div class="font-semibold text-gray-800">{{ playerDetails.team }}</div>
                </div>
              </div>
            </div>

            <!-- Personal Info -->
            <div v-if="playerDetails.age || playerDetails.birthdate || playerDetails.height_cm || playerDetails.nationality">
              <h4 class="font-semibold text-gray-700 flex items-center mb-3">
                <i class="fas fa-user-circle text-pink-500 mr-2"></i>
                Personal Information
              </h4>
              <div class="grid grid-cols-2 gap-4">
                <div v-if="playerDetails.age" class="bg-gray-50 rounded-lg p-4">
                  <div class="text-sm text-gray-500 mb-1">Age</div>
                  <div class="font-semibold text-gray-800">{{ playerDetails.age }} years</div>
                </div>
                <div v-if="playerDetails.birthdate" class="bg-gray-50 rounded-lg p-4">
                  <div class="text-sm text-gray-500 mb-1">Birthdate</div>
                  <div class="font-semibold text-gray-800">{{ playerDetails.birthdate }}</div>
                </div>
                <div v-if="playerDetails.height_cm" class="bg-gray-50 rounded-lg p-4">
                  <div class="text-sm text-gray-500 mb-1">Height</div>
                  <div class="font-semibold text-gray-800">{{ playerDetails.height_cm }} cm</div>
                </div>
                <div v-if="playerDetails.nationality" class="bg-gray-50 rounded-lg p-4">
                  <div class="text-sm text-gray-500 mb-1">Nationality</div>
                  <div class="font-semibold text-gray-800">{{ playerDetails.nationality }}</div>
                </div>
              </div>
            </div>

            <!-- External Links -->
            <div v-if="playerDetails.player_link">
              <h4 class="font-semibold text-gray-700 flex items-center mb-3">
                <i class="fas fa-external-link-alt text-pink-500 mr-2"></i>
                External Resources
              </h4>
              <a 
                :href="playerDetails.player_link" 
                target="_blank"
                class="flex items-center justify-between bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-4 hover:from-pink-100 hover:to-purple-100 transition-colors border border-pink-200"
              >
                <div class="flex items-center space-x-3">
                  <div class="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                    <i class="fas fa-basketball-ball text-pink-500"></i>
                  </div>
                  <div>
                    <div class="font-semibold text-gray-800">Ex Profile</div>
                    <div class="text-sm text-gray-500">View full player profile</div>
                  </div>
                </div>
                <i class="fas fa-arrow-right text-pink-500"></i>
              </a>
            </div>

            <!-- Metadata -->
            <div v-if="playerDetails.created_at || playerDetails.updated_at">
              <h4 class="font-semibold text-gray-700 flex items-center mb-3">
                <i class="fas fa-clock text-pink-500 mr-2"></i>
                Record Information
              </h4>
              <div class="grid grid-cols-2 gap-4">
                <div v-if="playerDetails.created_at" class="bg-gray-50 rounded-lg p-4">
                  <div class="text-sm text-gray-500 mb-1">Created</div>
                  <div class="font-semibold text-gray-800 text-sm">
                    {{ new Date(playerDetails.created_at).toLocaleString('et-EE', { 
                      day: '2-digit', 
                      month: '2-digit', 
                      year: '2-digit', 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    }).replace(',', '') }}
                  </div>
                </div>
                <div v-if="playerDetails.updated_at" class="bg-gray-50 rounded-lg p-4">
                  <div class="text-sm text-gray-500 mb-1">Last Updated</div>
                  <div class="font-semibold text-gray-800 text-sm">
                    {{ new Date(playerDetails.updated_at).toLocaleString('et-EE', { 
                      day: '2-digit', 
                      month: '2-digit', 
                      year: '2-digit', 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    }).replace(',', '') }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Raw Data Display (for debugging/development) -->
            <details class="bg-gray-50 rounded-lg p-4">
              <summary class="cursor-pointer font-semibold text-gray-700 flex items-center">
                <i class="fas fa-code text-gray-400 mr-2"></i>
                Raw Data
              </summary>
              <pre class="mt-3 text-xs bg-gray-800 text-gray-100 p-3 rounded overflow-x-auto">{{ JSON.stringify(playerDetails, null, 2) }}</pre>
            </details>
          </div>
        </div>
      </div>
    </div>
  </div>
</template> 